/**
 * Shared, pure enemy-AI module.
 *
 * Every function here is free of React, DOM, or Worker globals so it can be
 * imported from both the main-thread hook (useEnemyEngine) and the web worker
 * (enemy.worker.ts) without duplication.
 */
import type { Direction, Position, Collectible } from '@/types/game';
import type { EnemyState, EnemyAIState, WorldEnemyType } from '@/types/enemy';
import type { EnemyDifficultyConfig } from '@/config/enemyDifficulty';
import { ENEMY_DIFFICULTY } from '@/config/enemyDifficulty';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ALL_DIRECTIONS: Direction[] = ['up', 'down', 'left', 'right'];

export function reverseDirection(dir: Direction): Direction {
  switch (dir) {
    case 'up': return 'down';
    case 'down': return 'up';
    case 'left': return 'right';
    case 'right': return 'left';
  }
}

/** Rotate 90 degrees clockwise (used by square-patrol beetles). */
function rotateCW(dir: Direction): Direction {
  switch (dir) {
    case 'up': return 'right';
    case 'right': return 'down';
    case 'down': return 'left';
    case 'left': return 'up';
  }
}

function manhattanDistance(a: Position, b: Position): number {
  return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]);
}

function randomDirection(): Direction {
  return ALL_DIRECTIONS[Math.floor(Math.random() * ALL_DIRECTIONS.length)];
}

// ---------------------------------------------------------------------------
// Sliding physics (shared by player and enemies)
// ---------------------------------------------------------------------------

/**
 * Slides an entity from startPos in the given direction until it hits a wall,
 * rock, lava, stop tile, another obstacle, or the grid edge.
 * This uses the same ice-sliding mechanics as the player.
 */
export function slideInDirection(
  startPos: Position,
  direction: Direction,
  grid: string[],
  gridWidth: number,
  obstacles: Position[],
): Position {
  const [col, row] = startPos;
  let newCol = col;
  let newRow = row;

  const dx = direction === 'left' ? -1 : direction === 'right' ? 1 : 0;
  const dy = direction === 'up' ? -1 : direction === 'down' ? 1 : 0;

  while (true) {
    const nextCol = newCol + dx;
    const nextRow = newRow + dy;

    // Check grid boundaries
    if (nextRow < 0 || nextRow >= grid.length || nextCol < 0 || nextCol >= gridWidth) break;

    // Check what is at the next position in the grid
    const cell = grid[nextRow]?.[nextCol]?.toLowerCase();

    // Blocked by walls/rocks/lava
    if (cell === 'x' || cell === 'l') break;

    // Check if another obstacle (enemy/player) is there
    if (obstacles.some(([c, r]) => c === nextCol && r === nextRow)) break;

    // Move to next position
    newCol = nextCol;
    newRow = nextRow;

    // Stop tiles cause the slider to stop ON them
    if (cell === 's' || cell === 't' || cell === 'n') break;
  }

  return [newCol, newRow];
}

// ---------------------------------------------------------------------------
// Vision cone detection
// ---------------------------------------------------------------------------

const DIRECTION_DELTAS: Record<Direction, [number, number]> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

/**
 * Checks whether the player is within the enemy's forward vision cone.
 *
 * The cone extends `visionRange` tiles in the enemy's facing direction.
 * At each distance step, the perpendicular spread is `floor(visionSpread/2)`
 * tiles on each side. Line-of-sight is blocked by rocks and lava.
 */
export function isPlayerInVisionCone(
  enemy: EnemyState,
  playerPos: Position,
  grid: string[],
  gridWidth: number,
  config: EnemyDifficultyConfig,
): boolean {
  if (config.visionRange <= 0) return false;

  const facing = enemy.aiState?.facingDirection ?? enemy.direction;
  const [fdx, fdy] = DIRECTION_DELTAS[facing];

  // Perpendicular axis
  const [pdx, pdy] = fdy !== 0 ? [1, 0] : [0, 1];
  const halfSpread = Math.floor(config.visionSpread / 2);

  const [ex, ey] = enemy.position;
  const rows = grid.length;

  for (let dist = 1; dist <= config.visionRange; dist++) {
    const centerX = ex + fdx * dist;
    const centerY = ey + fdy * dist;

    // If the center tile is out of bounds or blocked, stop looking further
    if (centerY < 0 || centerY >= rows || centerX < 0 || centerX >= gridWidth) break;
    const centerCell = grid[centerY]?.[centerX]?.toLowerCase();
    if (centerCell === 'x' || centerCell === 'l') break;

    // Check all tiles at this distance within the spread
    for (let offset = -halfSpread; offset <= halfSpread; offset++) {
      const tx = centerX + pdx * offset;
      const ty = centerY + pdy * offset;

      if (ty < 0 || ty >= rows || tx < 0 || tx >= gridWidth) continue;

      // Skip blocked perpendicular tiles (but don't break the main loop)
      if (offset !== 0) {
        const cell = grid[ty]?.[tx]?.toLowerCase();
        if (cell === 'x' || cell === 'l') continue;
      }

      if (tx === playerPos[0] && ty === playerPos[1]) return true;
    }
  }

  return false;
}

// ---------------------------------------------------------------------------
// AI decision functions -- each returns a Direction
// ---------------------------------------------------------------------------

/**
 * Patrol AI (Bramble Beetle)
 *
 * patrolComplexity 2: continues in current direction; reverses on wall hit.
 * patrolComplexity 4: rotates 90deg clockwise on wall hit, creating a square
 * patrol route.
 */
function patrolAI(
  enemy: EnemyState,
  grid: string[],
  gridWidth: number,
  obstacles: Position[],
  config: EnemyDifficultyConfig,
): { direction: Direction; patrolPhase?: number } {
  const result = slideInDirection(enemy.position, enemy.direction, grid, gridWidth, obstacles);
  const blocked = result[0] === enemy.position[0] && result[1] === enemy.position[1];

  if (!blocked) {
    return { direction: enemy.direction };
  }

  // Wall hit -- decide how to redirect
  if (config.patrolComplexity === 4) {
    // Square patrol: rotate clockwise through up to 3 attempts
    let dir = enemy.direction;
    const phase = (enemy.patrolPhase ?? 0) + 1;
    for (let attempt = 0; attempt < 3; attempt++) {
      dir = rotateCW(dir);
      const test = slideInDirection(enemy.position, dir, grid, gridWidth, obstacles);
      if (test[0] !== enemy.position[0] || test[1] !== enemy.position[1]) {
        return { direction: dir, patrolPhase: phase % 4 };
      }
    }
    // All directions blocked, just reverse (fallback)
    return { direction: reverseDirection(enemy.direction), patrolPhase: phase % 4 };
  }

  // Default: simple back-and-forth
  return { direction: reverseDirection(enemy.direction) };
}

/**
 * Stalk AI (Tunnel Drone)
 *
 * Picks the axis with the larger gap between enemy and target, then slides
 * toward the target on that axis.
 *
 * - With `mistakeRate` probability, picks a random direction instead.
 * - Randomises tiebreaker on equal-distance axes (was always horizontal before).
 */
function stalkAI(
  enemy: EnemyState,
  targetPos: Position,
  config: EnemyDifficultyConfig,
): Direction {
  // Mistake: random direction
  if (config.mistakeRate > 0 && Math.random() < config.mistakeRate) {
    return randomDirection();
  }

  const dx = targetPos[0] - enemy.position[0];
  const dy = targetPos[1] - enemy.position[1];
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);

  if (absDx === absDy) {
    // Randomise tiebreaker instead of always preferring horizontal
    if (Math.random() < 0.5) {
      return dx >= 0 ? 'right' : 'left';
    }
    return dy >= 0 ? 'down' : 'up';
  }

  if (absDx > absDy) {
    return dx >= 0 ? 'right' : 'left';
  }
  return dy >= 0 ? 'down' : 'up';
}

/**
 * Bounce AI (Skid Mite)
 *
 * Returns a random cardinal direction. Improvements:
 * - Filters out directions that cause zero movement (wall right next to enemy).
 * - With `positionBias` probability, biases toward nearest collectible or player.
 */
function bounceAI(
  enemy: EnemyState,
  grid: string[],
  gridWidth: number,
  obstacles: Position[],
  playerPos: Position,
  collectibles: Collectible[],
  config: EnemyDifficultyConfig,
): Direction {
  // Filter out directions that produce no movement
  const viableDirections = ALL_DIRECTIONS.filter((dir) => {
    const dest = slideInDirection(enemy.position, dir, grid, gridWidth, obstacles);
    return dest[0] !== enemy.position[0] || dest[1] !== enemy.position[1];
  });

  if (viableDirections.length === 0) {
    // Completely stuck -- return a random direction (will be a no-op anyway)
    return randomDirection();
  }

  // Position bias: move toward an "interesting" position
  if (config.positionBias > 0 && Math.random() < config.positionBias) {
    // Find nearest uncollected collectible
    const uncollected = collectibles.filter((c) => !c.isCollected);
    let target: Position = playerPos;
    let bestDist = manhattanDistance(enemy.position, playerPos);

    for (const c of uncollected) {
      const d = manhattanDistance(enemy.position, c.position);
      if (d < bestDist) {
        bestDist = d;
        target = c.position;
      }
    }

    // Pick the viable direction that best approaches the target
    let bestDir = viableDirections[0];
    let bestAfterDist = Infinity;
    for (const dir of viableDirections) {
      const dest = slideInDirection(enemy.position, dir, grid, gridWidth, obstacles);
      const d = manhattanDistance(dest, target);
      if (d < bestAfterDist) {
        bestAfterDist = d;
        bestDir = dir;
      }
    }
    return bestDir;
  }

  // Pure random from viable set
  return viableDirections[Math.floor(Math.random() * viableDirections.length)];
}

/**
 * Chase AI (Cinder Hound)
 *
 * Directly chases the player position.
 * - With `mistakeRate` probability, picks a random direction.
 * - 30% chance to attempt flanking (predict player's next position).
 */
function chaseAI(
  enemy: EnemyState,
  playerPos: Position,
  playerLastDir: Direction | null,
  config: EnemyDifficultyConfig,
): Direction {
  // Mistake: random direction
  if (config.mistakeRate > 0 && Math.random() < config.mistakeRate) {
    return randomDirection();
  }

  // Flanking: 30% chance to predict where the player is heading
  let target = playerPos;
  if (playerLastDir && Math.random() < 0.3) {
    const flankDx = playerLastDir === 'left' ? -2 : playerLastDir === 'right' ? 2 : 0;
    const flankDy = playerLastDir === 'up' ? -2 : playerLastDir === 'down' ? 2 : 0;
    target = [playerPos[0] + flankDx, playerPos[1] + flankDy];
  }

  // Use stalk logic toward the (possibly predicted) target
  return stalkAI(enemy, target, { ...config, mistakeRate: 0 });
}

/**
 * Mirror AI (Echo Mirror)
 *
 * Copies the player's direction with a 1-turn memory delay.
 * Uses mirrorMemory to store the last 2 player directions and returns
 * the one from 2 moves ago (index 0), not the most recent.
 */
function mirrorAI(
  enemy: EnemyState,
  playerLastDirection: Direction | null,
): { direction: Direction; mirrorMemory: (Direction | null)[] } {
  // Build updated memory ring buffer (keep last 2 entries)
  const prevMemory = enemy.mirrorMemory ?? [null, null];
  const updatedMemory: (Direction | null)[] = [
    prevMemory.length > 1 ? prevMemory[1] : null,
    playerLastDirection,
  ];

  // Use the direction from 2 moves ago (index 0), fallback to 'right'
  const delayed = updatedMemory[0] ?? 'right';

  return { direction: delayed, mirrorMemory: updatedMemory };
}

// ---------------------------------------------------------------------------
// Direction resolver -- dispatches to the correct AI based on enemy type
// ---------------------------------------------------------------------------

export interface AIContext {
  playerPos: Position;
  playerPrevPos: Position;
  playerLastDir: Direction | null;
  grid: string[];
  gridWidth: number;
  obstacles: Position[];
  collectibles: Collectible[];
}

/**
 * Determines the direction an enemy should move and any state patches.
 * Returns both the chosen direction and optional EnemyState fields to merge.
 *
 * Vision-aware enemies (visionRange > 0) check for the player in their
 * forward cone before dispatching to type-specific AI. If the player is
 * spotted, the enemy enters chase mode for `chaseDuration` turns.
 */
export function getEnemyDirection(
  enemy: EnemyState,
  ctx: AIContext,
): { direction: Direction; patch?: Partial<EnemyState> } {
  const config = ENEMY_DIFFICULTY[enemy.type as WorldEnemyType];

  // skipTurnChance: enemy occasionally skips a turn
  if (config.skipTurnChance > 0 && Math.random() < config.skipTurnChance) {
    return { direction: enemy.direction };
  }

  // accuracy: with (1-accuracy) probability, pick a random direction
  if (config.accuracy < 1 && Math.random() > config.accuracy) {
    return { direction: randomDirection() };
  }

  // --- Vision & Chase state management ---
  let currentAI = enemy.aiState;
  let isNowChasing = currentAI?.isChasing ?? false;
  let chaseTurnsRemaining = currentAI?.chaseTurnsRemaining ?? 0;
  let lastKnownPlayerPos = currentAI?.lastKnownPlayerPos;

  if (config.visionRange > 0) {
    const spotted = isPlayerInVisionCone(enemy, ctx.playerPos, ctx.grid, ctx.gridWidth, config);

    if (spotted) {
      isNowChasing = true;
      chaseTurnsRemaining = config.chaseDuration;
      lastKnownPlayerPos = ctx.playerPos;
    } else if (isNowChasing) {
      chaseTurnsRemaining--;
      if (chaseTurnsRemaining <= 0) {
        isNowChasing = false;
        chaseTurnsRemaining = 0;
        lastKnownPlayerPos = undefined;
      }
    }
  }

  // If chasing, override normal AI with direct chase toward last known position
  if (isNowChasing && lastKnownPlayerPos) {
    const chaseDir = chaseAI(enemy, lastKnownPlayerPos, ctx.playerLastDir, config);
    const aiPatch: EnemyAIState = {
      isChasing: isNowChasing,
      chaseTurnsRemaining,
      lastKnownPlayerPos,
      facingDirection: chaseDir,
    };
    return { direction: chaseDir, patch: { aiState: aiPatch } };
  }

  // --- Type-specific AI ---
  let direction: Direction;
  let extraPatch: Partial<EnemyState> | undefined;

  switch (enemy.type as WorldEnemyType) {
    case 'bramble_beetle': {
      const result = patrolAI(enemy, ctx.grid, ctx.gridWidth, ctx.obstacles, config);
      direction = result.direction;
      extraPatch = result.patrolPhase !== undefined ? { patrolPhase: result.patrolPhase } : undefined;
      break;
    }
    case 'tunnel_drone':
      direction = stalkAI(enemy, ctx.playerPrevPos, config);
      break;
    case 'skid_mite':
      direction = bounceAI(
        enemy, ctx.grid, ctx.gridWidth, ctx.obstacles,
        ctx.playerPos, ctx.collectibles, config,
      );
      break;
    case 'cinder_hound':
      direction = chaseAI(enemy, ctx.playerPos, ctx.playerLastDir, config);
      break;
    case 'echo_mirror': {
      const result = mirrorAI(enemy, ctx.playerLastDir);
      direction = result.direction;
      extraPatch = { mirrorMemory: result.mirrorMemory };
      break;
    }
    default:
      direction = 'right';
  }

  // Update aiState with the new facing direction (even when not chasing)
  const aiPatch: EnemyAIState = {
    isChasing: isNowChasing,
    chaseTurnsRemaining,
    lastKnownPlayerPos,
    facingDirection: direction,
  };

  return {
    direction,
    patch: { ...extraPatch, aiState: aiPatch },
  };
}

// ---------------------------------------------------------------------------
// Batch movement
// ---------------------------------------------------------------------------

/**
 * Synchronously moves all enemies one step. This is the authoritative
 * movement logic used by both the main thread fallback and the web worker.
 */
export function moveAllEnemiesSync(
  enemies: EnemyState[],
  grid: string[],
  gridWidth: number,
  playerPosition: Position,
  playerPrevPosition: Position,
  playerLastDirection: Direction | null,
  collectibles: Collectible[] = [],
): EnemyState[] {
  const updatedEnemies = enemies.map((e) => ({ ...e }));

  for (let i = 0; i < updatedEnemies.length; i++) {
    const enemy = updatedEnemies[i];

    if (enemy.isStunned) {
      enemy.isStunned = false;
      continue;
    }

    if (enemy.turnsUntilActive > 0) {
      enemy.turnsUntilActive--;
      continue;
    }

    const obstacles: Position[] = [
      playerPosition,
      ...updatedEnemies.filter((_, j) => j !== i).map((e) => e.position),
    ];

    const ctx: AIContext = {
      playerPos: playerPosition,
      playerPrevPos: playerPrevPosition,
      playerLastDir: playerLastDirection,
      grid,
      gridWidth,
      obstacles,
      collectibles,
    };

    const { direction: dir, patch } = getEnemyDirection(enemy, ctx);

    const newPos = slideInDirection(enemy.position, dir, grid, gridWidth, obstacles);

    // Track failed direction if no movement occurred
    const moved = newPos[0] !== enemy.position[0] || newPos[1] !== enemy.position[1];

    enemy.previousPosition = [...enemy.position] as Position;
    enemy.position = newPos;
    enemy.direction = dir;
    enemy.lastFailedDirection = moved ? undefined : dir;

    // Merge any AI-specific state patches
    if (patch) {
      Object.assign(enemy, patch);
    }
  }

  return updatedEnemies;
}

/**
 * Moves all enemies with multi-step support.
 *
 * Enemies whose `movesPerTurn` > 1 take additional slides in a single player
 * turn. Each step re-evaluates AI direction and obstacles so multi-move enemies
 * react to the board state after their first slide.
 */
export function moveAllEnemiesMultiStep(
  enemies: EnemyState[],
  grid: string[],
  gridWidth: number,
  playerPosition: Position,
  playerPrevPosition: Position,
  playerLastDirection: Direction | null,
  collectibles: Collectible[] = [],
): EnemyState[] {
  const maxMoves = Math.max(
    1,
    ...enemies.map(e => ENEMY_DIFFICULTY[e.type as WorldEnemyType].movesPerTurn),
  );

  let current = enemies.map(e => ({ ...e }));

  for (let step = 0; step < maxMoves; step++) {
    const stepResult = current.map((e) => ({ ...e }));

    for (let i = 0; i < stepResult.length; i++) {
      const enemy = stepResult[i];
      const config = ENEMY_DIFFICULTY[enemy.type as WorldEnemyType];

      // Only move enemies whose movesPerTurn allows this step
      if (step >= config.movesPerTurn) continue;

      // First step: apply activation/stun rules; subsequent steps skip them
      if (step === 0) {
        if (enemy.isStunned) {
          enemy.isStunned = false;
          continue;
        }
        if (enemy.turnsUntilActive > 0) {
          enemy.turnsUntilActive--;
          continue;
        }
      }

      const obstacles: Position[] = [
        playerPosition,
        ...stepResult.filter((_, j) => j !== i).map(e => e.position),
      ];

      const ctx: AIContext = {
        playerPos: playerPosition,
        playerPrevPos: playerPrevPosition,
        playerLastDir: playerLastDirection,
        grid,
        gridWidth,
        obstacles,
        collectibles,
      };

      const { direction: dir, patch } = getEnemyDirection(enemy, ctx);
      const newPos = slideInDirection(enemy.position, dir, grid, gridWidth, obstacles);

      const moved = newPos[0] !== enemy.position[0] || newPos[1] !== enemy.position[1];

      enemy.previousPosition = [...enemy.position] as Position;
      enemy.position = newPos;
      enemy.direction = dir;
      enemy.lastFailedDirection = moved ? undefined : dir;

      if (patch) {
        Object.assign(enemy, patch);
      }
    }

    current = stepResult;
  }

  return current;
}

// ---------------------------------------------------------------------------
// Spawn helpers (used by loadLevel in useGameEngine)
// ---------------------------------------------------------------------------

/**
 * Computes a deterministic initial direction for an enemy based on its grid
 * position and index in the spawn list.
 */
export function deterministicDirection(x: number, y: number, index: number): Direction {
  return ALL_DIRECTIONS[(x + y + index) % 4];
}

/**
 * Computes the activation delay for an enemy based on its Manhattan distance
 * from the player start tile. Close enemies get more delay so the player has
 * breathing room; far enemies activate sooner.
 */
export function computeActivationDelay(
  spawnPos: Position,
  startPos: Position,
  config: EnemyDifficultyConfig,
): number {
  const dist = manhattanDistance(spawnPos, startPos);
  // Close (dist <= 3): 2-3 turns delay. Far (dist > 6): 0-1 turns delay.
  if (dist <= 3) {
    return config.minActivationDelay + 2 + Math.floor(Math.random() * (config.maxActivationDelayBonus + 1));
  }
  if (dist <= 6) {
    return config.minActivationDelay + 1 + Math.floor(Math.random() * config.maxActivationDelayBonus);
  }
  return config.minActivationDelay + Math.floor(Math.random() * config.maxActivationDelayBonus);
}
